import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
// @ts-ignore
import introJs from 'intro.js';
import 'intro.js/introjs.css';

import Dashboard from '@/screens/dashboard/dashboard';
import Allocation from '@/screens/allocation/allocation';
import Attestation from '@/screens/attestation/attestation';
import Optimization from '@/screens/optimization/optimization';
import Budgets from '@/screens/budgets/budgets';
import Reporting from '@/screens/reporting/reporting';
import Disputes from '@/screens/disputes/disputes';
import Administration from '@/screens/administration/administration';
import Ai from '@/screens/ai/ai';

import { ROUTES, urls } from '@/urls';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { getUser, subscribeToSession } from '@/lib/common';
import { useAuth } from '@/auth/AuthProvider';

import Sidebar from './sidebar';
import Topbar from './topbar';
import CustomToastContainer from './custom-toast-container';
import { NAV_LINKS, pageTitleForPath } from './nav-model';
import { ChartTooltip } from '@/components/ds/charts';

export default function AppShell({ user }: { user: any }) {
  const location = useLocation();
  const { signOut } = useAuth();
  const snapshot = getUser();

  const [, setSessionVersion] = useState(0);
  const [config, setConfig] = useState<any>(null);
  const [unreadSummary, setUnreadSummary] = useState({ unreadDisputes: 0, unreadMessages: 0 });
  const [demoBubbleVisible, setDemoBubbleVisible] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const demoEnabled = !!config?.enable_demo_generator;

  // ---- re-render on session changes (e.g. currency switched in the topbar) ----
  useEffect(
    () =>
      subscribeToSession(() => {
        setSessionVersion((version) => version + 1);
      }),
    [],
  );

  // ---- config poll (demo generator gate) — every 5 min ----
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await fetchWrapper.get(urls.DevOps.getConfig);
        setConfig(data);
      } catch (error) {
        console.error('Failed to fetch config:', error);
      }
    };
    fetchConfig();
    const interval = setInterval(fetchConfig, 300000);
    return () => clearInterval(interval);
  }, []);

  // ---- dispute unread summary — every 15s ----
  useEffect(() => {
    const loadUnreadSummary = () => {
      fetchWrapper
        .get(urls.DevOps.getDisputeUnreadSummary)
        .then((data) => {
          setUnreadSummary({
            unreadDisputes: data?.disputes_with_unread ?? 0,
            unreadMessages: data?.unread_messages ?? 0,
          });
        })
        .catch((error) => console.error('Failed to fetch dispute unread summary:', error));
    };
    loadUnreadSummary();
    const summaryTimer = setInterval(loadUnreadSummary, 15000);
    return () => clearInterval(summaryTimer);
  }, []);

  // ---- notifications toast — every 15s ----
  const toastClosed = (toastId: any) => {
    fetchWrapper
      .post(urls.DevOps.updateNotification, { id: toastId })
      .catch((err) => console.error('Toast update error:', err));
  };
  const toastIt = useCallback((row: any) => {
    toast(
      <div>
        <b>{row.title}</b> <br /> {row.body}
      </div>,
      { toastId: row.id, onClose: () => toastClosed(row.id) },
    );
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      fetchWrapper
        .get(urls.DevOps.getNotifications)
        .then((data) => {
          data
            .filter((row: any) => Number(row.id_user) === Number(snapshot?.id))
            .forEach((row: any) => toastIt(row));
        })
        .catch((err) => console.error('Fetch error:', err));
    }, 15000);
    return () => clearInterval(timer);
  }, [toastIt, snapshot?.id]);

  // ---- demo bubble hover ----
  const onDemoEnter = () => {
    if (!demoEnabled) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setDemoBubbleVisible(true);
  };
  const onDemoLeave = () => {
    if (!demoEnabled) return;
    hideTimeoutRef.current = setTimeout(() => setDemoBubbleVisible(false), 200);
  };
  useEffect(
    () => () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    },
    [],
  );

  // ---- onboarding tour ----
  const startOnboarding = () => {
    const intro = introJs();
    const steps = NAV_LINKS.map((link) => ({
      element: `[data-intro-id="${link.route}"]`,
      intro: link.dataIntro,
      position: 'right',
    }));
    intro
      .setOptions({
        tooltipClass: 'customTooltip',
        steps: steps.filter((step) => document.querySelector(step.element)),
        showProgress: true,
        showButtons: true,
        exitOnOverlayClick: false,
        exitOnEsc: true,
      })
      .start();
  };

  const onLogout = () => {
    signOut().catch((err) => console.error('Logout failed', err));
  };

  const title = pageTitleForPath(location.pathname);
  const currency = snapshot?.default_currency ?? user?.default_currency ?? 'USD';

  return (
    <div className="cc-shell">
      <Sidebar
        user={snapshot || user}
        firstName={user?.firstName}
        unreadDisputes={unreadSummary.unreadDisputes}
        demoEnabled={demoEnabled}
        demoBubbleVisible={demoBubbleVisible}
        onDemoEnter={onDemoEnter}
        onDemoLeave={onDemoLeave}
        onDemoClose={() => setDemoBubbleVisible(false)}
        onLogout={onLogout}
      />
      <main className="cc-main">
        <Topbar
          title={title}
          currency={currency}
          unreadDisputes={unreadSummary.unreadDisputes}
          onHelp={startOnboarding}
        />
        <div className="cc-content" key={currency}>
          <Routes>
            <Route path={ROUTES.HOME} element={<Dashboard />} />
            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.ALLOCATION} element={<Allocation />} />
            <Route path={ROUTES.ATTESTATION} element={<Attestation />} />
            <Route path={ROUTES.BUDGETS} element={<Budgets />} />
            <Route path={ROUTES.OPTIMIZATION} element={<Optimization />} />
            <Route path={ROUTES.REPORTING} element={<Reporting />} />
            <Route path={ROUTES.DISPUTES} element={<Disputes />} />
            <Route path={ROUTES.AUTH_CALLBACK} element={<Dashboard />} />
            <Route path={ROUTES.ADMINISTRATION} element={<Administration />} />
            <Route path={ROUTES.AI} element={<Ai />} />
          </Routes>
        </div>
      </main>
      <CustomToastContainer />
      <ChartTooltip />
    </div>
  );
}
